import { useState } from 'react';
import { useConfig } from '../config.js';

// Home "What's new" card — the in-app changelog headline. Entries are
// curated in /admin (usually promoted from recent repository changes)
// and arrive via the config bundle. The card only shows when there's
// something the keeper hasn't acknowledged yet: "Got it" remembers the
// newest entry date per device, and the card stays gone until a newer
// entry ships.

const SEEN_KEY = 'aqs.whatsnew_seen';

function latestDate(entries) {
  return entries.reduce((max, e) => (e.date > max ? e.date : max), '');
}

export function WhatsNewCard({ order }) {
  const config = useConfig();
  const entries = (config?.whats_new?.entries || [])
    .filter((e) => e && e.title && e.date)
    .slice()
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 4);

  const [seen, setSeen] = useState(() => {
    try {
      return localStorage.getItem(SEEN_KEY) || '';
    } catch {
      return '';
    }
  });

  if (!entries.length) return null;
  const newest = latestDate(entries);
  if (newest <= seen) return null;

  const gotIt = () => {
    try {
      localStorage.setItem(SEEN_KEY, newest);
    } catch {
      /* private mode — card just shows again next visit */
    }
    setSeen(newest);
  };

  return (
    <section
      className="slide-in span-2"
      style={{
        order: typeof order === 'number' ? order : undefined,
        padding: '14px 16px',
        borderRadius: 14,
        background: 'var(--surface)',
        border: '1px solid var(--surface-border)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <h2 className="font-heading" style={{ fontSize: 15, letterSpacing: '0.08em' }}>
          WHAT'S NEW
        </h2>
        <button
          onClick={gotIt}
          className="font-heading hv-whatsnew-ok"
          style={{
            fontSize: 11,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}
        >
          Got it
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {entries.map((e, i) => (
          <div key={`${e.date}-${i}`} style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
            <span
              className="font-mono"
              style={{ fontSize: 12, color: 'var(--text-dim)', flexShrink: 0, minWidth: 66 }}
            >
              {e.date}
            </span>
            <div style={{ minWidth: 0 }}>
              <span style={{ fontSize: 16, color: 'var(--text)', fontWeight: 500 }}>{e.title}</span>
              {e.blurb && (
                <span style={{ fontSize: 15, color: 'var(--text-muted)' }}> — {e.blurb}</span>
              )}
              {e.link && (
                <>
                  {' '}
                  <a
                    href={e.link}
                    style={{ fontSize: 14, color: 'var(--cyan)' }}
                    target={e.link.startsWith('http') ? '_blank' : undefined}
                    rel="noopener noreferrer"
                  >
                    Try it →
                  </a>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <a
        href="/changelog"
        target="_blank"
        rel="noreferrer"
        style={{ display: 'inline-block', marginTop: 10, fontSize: 14, color: 'var(--cyan)' }}
      >
        See every update
      </a>
    </section>
  );
}
